import "@mdi/font/css/materialdesignicons.css"
import { watchEffect } from "vue"
import { createVuetify } from "vuetify"
import "vuetify/styles"
import { de, en } from "vuetify/locale"
import { readThemePreference } from "@/composables/useThemePreference"

const vuetify = createVuetify({
  // Vuetify's own locale, which drives its built-in strings *and* the weekday/month names in every
  // date picker. Read from the same `locale` key vue-i18n is initialised from (see plugins/i18n), so
  // the two never disagree on load.
  //
  // This previously used the Vuetify 2 `lang: { locales, current }` option plus a top-level
  // `adapter:` key — neither of which Vuetify 4 reads — so Vuetify silently stayed on English and
  // German users got "Mo Tu We Th Fr Sa Su" in the date pickers. Routing it through
  // `createVueI18nAdapter` instead would make Vuetify resolve its `$vuetify.*` keys from our message
  // files, which do not define them; using Vuetify's shipped packs keeps those strings translated.
  locale: {
    locale: localStorage.getItem("locale") ?? "de",
    fallback: "de",
    messages: { de, en },
  },
  // Restore Vuetify 3 breakpoints for the display composable / responsive props
  // (Vuetify 4 defaults to Material Design 3 values). Keep in sync with
  // $grid-breakpoints in src/styles/settings.scss.
  display: {
    thresholds: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1264,
      xl: 1904,
      xxl: 2560,
    },
  },
  date: {
    locale: {
      de: "de-DE",
      en: "de-DE",
    },
  },
  // House style for form controls, so density and field style are consistent by
  // construction rather than by remembering to type them. Every value here was the
  // overwhelming majority of the hand-written props it replaces (e.g. 130 of 132
  // v-text-field `variant` props were already "outlined"), so this encodes the
  // convention that already existed rather than introducing a new one.
  //
  // Each field component needs its own entry: VSelect & friends declare VTextField's
  // props as their own (makeVTextFieldProps) and hand them down via
  // VTextField.filterProps(), so a VTextField default would never reach them.
  //
  // Deliberate deviations stay explicit at the call site and now read as intentional:
  //   - the auth screens (Login, ForgotPassword, Registration) keep roomier fields via
  //     density="default"/"comfortable"
  //   - the 11 comfortable text fields and the two solo fields override as before
  // Not defaulted on purpose: VBtn (`text`/`flat`/`tonal` are three legitimate emphasis
  // levels, not drift, and 290 of 316 buttons carry no density at all), VCard, VChip,
  // VListItem, VRow and VTable — those have hundreds of unannotated usages that a
  // default would silently restyle.
  //
  // The pickers bring their own list, so `autocomplete: "off"` keeps Chrome's history
  // dropdown from opening on top of it. Vuetify sets no attribute by default, and Chrome
  // keys that history by the input's id, so the generated `input-v-…` ids, which repeat
  // across views, pulled in text typed into unrelated fields.
  defaults: {
    VTextField: { variant: "outlined", density: "compact" },
    VTextarea: { variant: "outlined", density: "compact" },
    VSelect: { variant: "outlined", density: "compact", autocomplete: "off" },
    VAutocomplete: { variant: "outlined", density: "compact", autocomplete: "off" },
    VCombobox: { variant: "outlined", density: "compact", autocomplete: "off" },
    VSwitch: { density: "compact" },
    VBtnToggle: { variant: "outlined" },
    VAlert: { variant: "tonal" },
  },
  theme: {
    // The user's persisted choice — "light", "dark", or "system" (Vuetify resolves the last one
    // from `prefers-color-scheme` and keeps tracking it via matchMedia). Read straight from
    // localStorage rather than from the layout store, because `createVuetify()` runs at module
    // evaluation, before `createStore()` in main.js has installed pinia. Same trick as `locale`
    // above; `readThemePreference()` validates the value, since an unknown theme name would leave
    // Vuetify with no current theme at all.
    defaultTheme: readThemePreference(),
    options: {
      customProperties: true,
    },
    themes: {
      // Two-tone, restrained palette: a single indigo-blue brand hue carries the
      // UI (app bar, buttons, links, active states, entity badges), with a warm
      // gold accent reserved for occasional emphasis. Everything else is a
      // neutral grey scale — colour signals meaning, it doesn't decorate.
      light: {
        colors: {
          background: "#F4F5F7",
          primary: "#2B4C7E",
          secondary: "#4A5568",
          accent: "#E0A458",
          error: "#D14343",
          info: "#3E6DA3",
          success: "#3F8F5B",
          warning: "#D9922E",
          // The two report types, told apart by colour wherever both can be on screen or one can be
          // mistaken for the other: the project's report tabs, their editors, the cost views. The
          // Stundenlohnbericht keeps the brand hue it always had there; the Bautagesbericht is the
          // teal the cost views introduced, darkened to 5.3:1 on white so it holds as text and
          // under a white button label.
          "construction-report": "#22776E",
          "hourly-wage-report": "#2B4C7E",
          // Neutral surface scale, defined as top-level theme colors so the
          // bg-surface-* utilities and --v-theme-surface-* vars are generated.
          "surface-base": "#ECEDEF",
          "surface-lighten1": "#F4F5F6",
          "surface-lighten2": "#FAFAFB",
          "surface-darken1": "#DEDFE3",
          "surface-darken2": "#BCC0C7",
          "surface-darken3": "#9AA0A9",
          "surface-darken4": "#6E7480",
          "surface-darken5": "#515660",
        },
      },
      // The same two-tone palette after dark, derived from the light one rather than invented:
      // every colour keeps its hue and saturation and is only re-lightened so that it lands on
      // `surface` at the SAME WCAG contrast ratio it has on white above. That is what keeps a
      // muted caption muted and an error red urgent in both themes, instead of one theme
      // shouting where the other whispers.
      //
      // Three roles are the exception, and are lifted to ~6-8:1 instead of matched: `accent`,
      // `warning` and `success` sit at 2.2-4.0:1 on white because they are used as saturated
      // *fills* under dark text. Contrast is symmetric, so ratio-matching them on a dark surface
      // would have produced a mud brown and a bottle green. They stay the same hue, lighter.
      //
      // A note on `error`/`info`/`success` before someone "fixes" them: lifting a colour makes it
      // better as text on a dark page and worse as a fill under the white foreground Vuetify then
      // picks for it (3.6 / 3.1 / 2.6:1 here). Pushing them ~10% lighter still would flip Vuetify
      // to a black foreground and score ~9:1 on both counts — at the cost of a pale-pink "error"
      // and a mint "success" that no longer read as urgent or as positive. They are left where
      // they are: the same band the light theme already ships (success 3.97, warning 2.59) and
      // better than Vuetify's own dark defaults (3.60 / 3.12 / 2.78) on every one of the three.
      dark: {
        colors: {
          // A deep navy, not a near-black — and definitely not the mid-grey #575757 this used to
          // be, which was lighter than most of the "surface" scale below it and made the whole
          // theme read inside-out. `surface` stays a step ABOVE the background, mirroring light
          // (#F4F5F7 background under white cards), so a card still lifts off the page.
          //
          // The chroma here is deliberate. A first pass carried the light theme's neutral
          // saturation (~13%) straight over, and it read as flat black: 13% on a near-WHITE
          // surface is a clean white, but 13% on a near-BLACK one is just black. These are the
          // same colours re-mixed at 26-34% of the brand hue, at IDENTICAL luminance — so the
          // whole contrast argument above survives unchanged (every ratio moved by <0.01) and
          // only the colour changed.
          background: "#101621",
          surface: "#181F2C",
          primary: "#A5BDE0",
          secondary: "#A6B0C0",
          accent: "#E4B075",
          error: "#D86161",
          info: "#6B96C7",
          success: "#67B183",
          warning: "#DCAB60",
          // Same hue and saturation as light, re-lightened to its light-theme contrast (5.5:1 on
          // `surface`), as above.
          "construction-report": "#30A69A",
          "hourly-wage-report": "#A5BDE0",
          // Vuetify's own dark defaults for these two are off-palette (`surface-bright` is the
          // lilac #ccbfd6, which VSwitch paints its thumb with); its light defaults are already
          // neutral, so only the dark theme needs to say anything. `surface-light` is also what
          // Vuetify paints a bare toolbar with, and what `LewToolBar` uses for the dark app bar.
          "surface-bright": "#252E3F",
          "surface-light": "#212A3A",
          // The neutral scale, mirrored: in light it runs from "indistinguishable from the
          // surface" (lighten2) to "maximum contrast against it" (darken5), so here it runs the
          // same distances in the other direction — the names describe their light-theme
          // direction, their ROLE (quiet panel → divider → muted text → emphatic text) is what
          // carries over.
          //
          // Saturation tapers DOWN as the step gets lighter (29% → 12%): a deep panel or divider
          // carries the navy happily, but the top of the scale is the muted- and emphatic-TEXT
          // step, and tinting text reads as a link or a state rather than as a quiet grey. So the
          // blue is in the surfaces you look past and drains out of the ones you read.
          "surface-lighten2": "#1B2230",
          "surface-lighten1": "#1E2635",
          "surface-base": "#222B3B",
          "surface-darken1": "#2A3446",
          "surface-darken2": "#3D495F",
          "surface-darken3": "#536179",
          "surface-darken4": "#7D899F",
          "surface-darken5": "#A7AEBA",
        },
      },
    },
  },
})

/**
 * Mirror the resolved theme onto `<html data-theme="light|dark">`.
 *
 * Vuetify already puts a `.v-theme--dark` class on the app root, but two of the third-party
 * stylesheets this app ships key their dark themes off a `data-theme` ATTRIBUTE instead, and both
 * had therefore never switched:
 *
 *   - `@antongub-lib/gantt-component-library` ships a complete `[data-theme=dark]` block (49
 *     variables, its whole grey ramp inverted) — the personnel-planning diagrams stayed light
 *     purely because nothing set the attribute.
 *   - `@vuepic/vue-datepicker` picks its theme from a `dark` PROP rather than an attribute, so it
 *     cannot be reached this way; `styles/globals.scss` remaps its `--dp-*` variables under the
 *     same selector instead.
 *
 * The attribute is on `<html>` rather than the app root deliberately: both libraries teleport
 * parts of themselves (menus, tooltips) out to `<body>`, where a selector rooted at the app
 * element would no longer reach them.
 *
 * `watchEffect` runs immediately, so the attribute is correct on first paint, and re-runs when the
 * user changes the preference *or* when "system" follows the OS flipping at sunset.
 */
watchEffect(() => {
  document.documentElement.dataset.theme = vuetify.theme.current.value.dark ? "dark" : "light"
})

/**
 * No form in this app submits natively — pressing Enter in a field must never reload the page.
 *
 * `v-form` emits `submit` and then, unless a listener called `preventDefault()`, falls back to the
 * browser's own `form.submit()` as soon as its rules pass. In a single-page app that is a full page
 * load of the current URL. The browser raises that submit event whenever Enter is pressed in a form
 * whose only text-like input is the focused one (implicit submission), and most of our forms bind
 * no `@submit` at all — so Enter in a one-field dialog, a tab form or the "Passwort vergessen" page
 * threw the page away together with everything typed into it. The `@keydown.enter.prevent` on
 * individual inputs across the app are earlier, local patches for the same thing.
 *
 * Cancelled once here instead of on each of the forms: a capture listener on `document` runs before
 * any listener on the `<form>` itself, so VForm already finds `defaultPrevented` set and skips its
 * fallback. The event still reaches the form, so a `@submit="save"` keeps working; only the native
 * navigation is gone. Nothing here posts a form to a server — every request goes through axios.
 */
document.addEventListener("submit", event => event.preventDefault(), true)

export default vuetify
